<template>
	<StandardModal
		open
		large
		:title="t('polls_create_title')"
		:confirmText="t('common_button_send')"
		:cancelText="t('common_button_cancel')"
		@confirm="createNewPoll"
		@cancel="emit('close')"
		:confirmProps="{ loading: isLoading, disabled: isSendDisabled }"
		class="create-poll-modal"
		ref="modal"
	>
		<div class="create-poll-container">
			<FormItem label="Question" class="question-form-item">
				<Input
					:placeholder="t('polls_question_placeholder')"
					v-model="question"
					:maxLength="300"
					autoFocus
				/>
			</FormItem>

			<OptionsInput ref="optionsInput" />

			<Accordian
				:title="t('polls_settings_section_title')"
				class="poll-settings"
			>
				<div class="poll-settings-container">
					<div class="accordian-container">
						<div class="icon-container">
							<SquarePollHorizontalClockIcon
								type="regular"
								class="clock-icon"
							/>
						</div>
						<div class="text-container">
							<div class="title">{{ t('polls_closes_setting_title') }}</div>
						</div>
						<div class="control-container">
							<Dropdown
								v-model="closingDate"
								global
								small
								showOverlay
								:options="closeOptions"
								:optionsClass="`poll-closes-options`"
								@open="dropdownOpen"
								@change="onChange"
							>
								<template v-slot="{ option }">
									<div
										:class="{
											'selected-option':
												closingDate === option.id ||
												(!closingDate && option.id === 'CUSTOM'),
										}"
									>
										<span>{{ option.label }}</span>
										<CheckIcon
											class="checkmark-icon"
											type="solid"
											v-if="
												closingDate === option.id ||
												(!closingDate && option.id === 'CUSTOM')
											"
										/>
									</div>
								</template>
							</Dropdown>
						</div>
					</div>

					<div class="accordian-container" @click="toggleMultipleVotes">
						<div class="icon-container">
							<ListCheckIcon type="regular" class="list-check-icon" />
						</div>
						<div class="text-container">
							<div class="title">{{ t('polls_multiple_answers_title') }}</div>
						</div>
						<div class="control-container">
							<ToggleSwitch
								v-model="allowMultipleVotes"
								@click.stop
								ref="multipleVotesToggle"
							/>
						</div>
					</div>

					<div class="accordian-container" @click="togglePinPoll">
						<div class="icon-container">
							<ThumbtackIcon type="regular" class="thumbtack-icon" />
						</div>
						<div class="text-container">
							<div class="title">{{ t('polls_pin_poll_title') }}</div>
							<div class="description">
								{{ t('polls_pin_explanation') }}
							</div>
						</div>
						<div class="control-container">
							<ToggleSwitch v-model="pinPoll" @click.stop ref="pinPollToggle" />
						</div>
					</div>
				</div>
			</Accordian>

			<Accordian
				:title="t('polls_voter_identities_section_title')"
				:description="t('polls_voter_identities_subtitle')"
				class="voter-privacy"
			>
				<RadioGroup
					:options="voterOptions"
					v-model="selectedVoterSetting"
					class="radio-group-container"
					borderless
					large
				>
					<template #item="{ data }">
						<div class="accordian-container radio-group-item">
							<div class="icon-container">
								<component
									:is="data.icon"
									type="regular"
									:class="data.iconClass"
								/>
							</div>
							<div class="text-container">
								<div class="title">{{ data.label }}</div>
								<div class="description">{{ data.description }}</div>
							</div>

							<div class="control-container">
								<CircleCheckIcon
									v-if="data.value === selectedVoterSetting"
									type="solid"
								/>
								<CircleIcon v-else type="regular" />
							</div>
						</div>
					</template>
				</RadioGroup>
			</Accordian>

			<Accordian
				:title="t('polls_reveal_timing_section_title')"
				:description="t('polls_reveal_timing_subtitle')"
				class="results-timing"
			>
				<RadioGroup
					:options="resultsOptions"
					v-model="selectedResultsSetting"
					class="radio-group-container"
					borderless
					large
				>
					<template #item="{ data }">
						<div class="accordian-container radio-group-item">
							<div class="text-container">
								<div class="title">{{ data.label }}</div>
								<div class="description">{{ data.description }}</div>
							</div>

							<div class="control-container">
								<CircleCheckIcon
									v-if="data.value === selectedResultsSetting"
									type="solid"
								/>
								<CircleIcon v-else type="regular" />
							</div>
						</div>
					</template>
				</RadioGroup>
			</Accordian>
		</div>

		<DatePickerModal
			v-if="showDatePicker"
			v-model="customDate"
			:confirmText="t('common_date_select')"
			:cancelText="t('common_button_cancel')"
			showTime
			noPastDates
			@close="showDatePicker = false"
			@confirm="confirmCustomDate"
		/>
	</StandardModal>
</template>

<script setup>
import { ref, markRaw, defineEmits, computed } from 'vue'
import {
	StandardModal,
	FormItem,
	Input,
	Accordian,
	Dropdown,
	ToggleSwitch,
	RadioGroup,
	DatePickerModal,
} from '@/components/common'
import {
	SquarePollHorizontalClockIcon,
	ListCheckIcon,
	ThumbtackIcon,
	EarthIcon,
	UserLockIcon,
	EyeSlashIcon,
	CheckIcon,
	CircleCheckIcon,
	CircleIcon,
} from '@/icons'
import { OptionsInput } from '@/components/Polls'
import moment from 'moment'
import { createPoll } from '@/api/poll'
import { getCurrentChat } from '@/store'
import { useI18n } from 'vue-i18n'
import { useTemplateRef } from 'vue'

const props = defineProps({
	parentMessageId: {
		type: [Number, String],
		required: false,
	},
	chatId: {
		type: [Number, String],
		default: null,
	},
})

const { t } = useI18n()
const emit = defineEmits(['close'])

// Reactive state
const optionsInput = ref(null)
const showDatePicker = ref(false)
const customDate = ref(moment().add(24, 'hours').format('YYYY-MM-DD HH:mm'))
const isLoading = ref(false)
const question = ref(null)
const modal = useTemplateRef('modal')
const multipleVotesToggle = useTemplateRef('multipleVotesToggle')
const pinPollToggle = useTemplateRef('pinPollToggle')
const closeOptions = computed(() => [
	{ id: '1H', label: t('accessibility_hours_label.variable', { Count: 1 }, 1) },
	{ id: '4H', label: t('accessibility_hours_label.variable', { Count: 4 }, 4) },
	{
		id: '12H',
		label: t('accessibility_hours_label.variable', { Count: 12 }, 12),
	},
	{
		id: '24H',
		label: t('accessibility_hours_label.variable', { Count: 24 }, 24),
	},
	{ id: '3D', label: t('accessibility_days_label.variable', { Count: 3 }, 3) },
	{ id: '1W', label: t('accessibility_days_label.variable', { Count: 7 }, 7) }, // 1 week
	{ id: 'SEPARATOR', label: '', noHover: true },
	{ id: 'CUSTOM', label: t('polls_close_date_custom') },
])

const closingDate = ref('24H')
const allowMultipleVotes = ref(false)
const pinPoll = ref(false)
const selectedVoterSetting = ref('member')
const selectedResultsSetting = ref('always')
const isSendDisabled = computed(() => {
	return !question.value || optionsInput.value?.getCurrentOptions().length < 2
})
const voterOptions = computed(() => [
	{
		value: 'member',
		label: t('polls_voter_visibility_anyone_title'),
		description: t('polls_voter_visibility_anyone_subtitle'),
		icon: markRaw(EarthIcon),
		iconClass: 'globe-icon',
	},
	{
		value: 'owner',
		label: t('polls_voter_visibility_owners_title'),
		description: t('polls_voter_visibility_owners_subtitle'),
		icon: markRaw(UserLockIcon),
		iconClass: 'user-lock-icon',
	},
	{
		value: 'system',
		label: t('polls_voter_visibility_anonymous_title'),
		description: t('polls_voter_visibility_anonymous_subtitle'),
		icon: markRaw(EyeSlashIcon),
		iconClass: 'eye-slash-icon',
	},
])

const resultsOptions = computed(() => [
	{
		value: 'always',
		label: t('polls_reveal_timing_anytime_title'),
		description: t('polls_reveal_timing_anytime_subtitle'),
	},
	{
		value: 'after_vote',
		label: t('polls_reveal_timing_after_voting_title'),
		description: t('polls_reveal_timing_after_voting_subtitle'),
	},
	{
		value: 'after_close',
		label: t('polls_reveal_timing_after_closing_title'),
		description: t('polls_reveal_timing_after_closing_subtitle'),
	},
])

// Methods
const createNewPoll = async () => {
	try {
		isLoading.value = true
		const payload = {
			question: question.value,
			closes_at: getClosingDate(),
			allows_multiple_votes: allowMultipleVotes.value,
			auto_pinned: pinPoll.value,
			reveal_timing: selectedResultsSetting.value,
			chat_voter_visibility: selectedVoterSetting.value,
			options: optionsInput.value?.getCurrentOptions(),
			...(props.parentMessageId && {
				parent_message_id: props.parentMessageId,
			}),
		}
		const chatId = props.chatId ?? getCurrentChat().id
		const resp = await createPoll(chatId, payload)
		// console.log('New poll created:', resp)
		emit('close')
		isLoading.value = false
	} catch (error) {
		console.error(error)
		modal.value.$alert.error(error)
	} finally {
		isLoading.value = false
	}
}

const dropdownOpen = () => {
	// console.log('Dropdown open')
}

const onChange = (option) => {
	if (option === 'CUSTOM') {
		showDatePicker.value = true
	} else {
		const customOption = closeOptions.value.find(
			(option) => option.id === 'CUSTOM'
		)
		if (customOption) {
			customOption.label = 'Custom'
		}
	}
}

function confirmCustomDate(date) {
	if (!date) return
	// Format the date from "2025-05-31 12:34" to "5/31/25, 12:34 PM"
	const formattedDate = moment(date).format('M/D/YY, h:mm A')

	// Find and update the CUSTOM option
	const customOption = closeOptions.value.find(
		(option) => option.id === 'CUSTOM'
	)
	if (customOption) {
		customOption.label = formattedDate
	}
}

function toggleMultipleVotes() {
	multipleVotesToggle.value.toggle()
}

function togglePinPoll() {
	pinPollToggle.value.toggle()
}

const getClosingDate = () => {
	const now = moment().utc()

	switch (closingDate.value) {
		case '1H':
			return now.add(1, 'hour')
		case '4H':
			return now.add(4, 'hours')
		case '12H':
			return now.add(12, 'hours')
		case '24H':
			return now.add(24, 'hours')
		case '3D':
			return now.add(3, 'days')
		case '1W':
			return now.add(1, 'week')
		case 'CUSTOM':
			return moment(customDate.value)
		default:
			return now.add(24, 'hours')
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.create-poll-container {
	display: flex;
	flex-direction: column;
	padding: 24px 64px;
	:deep(.form-item-label) {
		font-size: 22px !important;
		font-weight: normal !important;
		font-family: 'Avenir-Heavy' !important;
	}

	.question-form-item {
		:deep(.pronto-input) {
			background-color: var(--neutral-100);
		}
	}

	.poll-options-container {
		margin-top: 20px;
	}
	.helper-text {
		font-size: 14px;
		color: var(--neutral-600);
		font-family: 'Avenir-Roman';
	}

	.line-break {
		width: 100%;
		height: 1px;
		background-color: var(--neutral-200);
	}

	.poll-settings {
		margin-top: 20px;
	}

	.poll-settings,
	.voter-privacy,
	.results-timing {
		border-top: 1px solid var(--neutral-200);
	}

	.accordian-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;

		.icon-container {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 52px;
			height: 52px;
			background-color: var(--neutral-100);
			border-radius: 6px;
			.pronto-icon {
				font-size: 35px;
				color: var(--neutral-500);
				&.globe-icon {
					font-size: 25px;
				}
				&.list-check-icon {
					font-size: 29px;
				}
				&.thumbtack-icon {
					font-size: 24px;
				}
				&.eye-slash-icon {
					font-size: 27px;
				}
				&.user-lock-icon {
					font-size: 30px;
				}
			}
		}

		.text-container {
			flex: 1; // takes up the remaining space
			padding: 0 16px; // space between icon and control

			.title {
				color: var(--neutral-800);
				font-family: 'Avenir-Heavy';
				font-size: 19px;
				// margin-bottom: 4px;
			}

			.description {
				color: #666;
				font-size: 13px;
			}
		}

		.control-container {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			flex-shrink: 0;

			.pronto-toggle {
				font-size: 27px;
				&.active {
					background-color: var(--teal-300);
				}
			}
		}
	}

	.radio-group-container {
		.radio-group-item {
			width: 100%;
		}
		:deep(.pronto-radio-item) {
			padding: 0;
			.control-container {
				.pronto-icon {
					color: var(--neutral-400);
				}
			}
			&.selected {
				.control-container {
					.pronto-icon {
						color: var(--teal-300);
					}
				}
			}
		}
	}
	.results-timing {
		.radio-group-item {
			.text-container {
				padding: 0;
			}
		}
	}
}
</style>

<style lang="scss">
.create-poll-container {
	.dropdown-container.small {
		.dropdown-selected-label {
			font-family: 'Avenir-Roman';
			font-size: 16px !important;
			color: var(--neutral-600);
		}
	}
}
.poll-closes-options {
	$height: 285px;
	width: 240px !important;
	&.open {
		max-height: $height !important;
	}

	.intersection-root {
		max-height: $height !important;
	}

	.dropdown-option-content {
		font-size: 16px;
		font-family: 'Avenir-Medium';
		color: var(--neutral-800);
		.checkmark-icon {
			font-size: 16px;
			color: var(--blue-300);
			margin-left: auto;
		}
		.selected-option {
			display: flex;
			justify-content: space-between;
			color: var(--blue-300);
		}
	}

	.dropdown-options {
		padding: 5px 10px 5px 10px !important;
		overflow: hidden !important;
		.dropdown-option {
			width: 100%;
			&.no-hover {
				padding: 0 !important;
				height: 1px !important;
				margin: 4px 0px !important;
				background: var(--neutral-200) !important;
				cursor: default !important;

				.dropdown-option-content {
					display: none;
				}
			}
		}
	}
}
.create-poll-modal {
	.modal-content {
		height: 90vh !important;
	}

	// Prevent modal body scrolling when dropdown is open
	&:has(.poll-closes-options.open) .modal-body {
		overflow: hidden !important;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.create-poll-modal {
		.modal-container {
			background: var(--neutral-0);
			.text-container {
				.description {
					color: var(--neutral-600);
				}
			}
		}
	}
	.create-poll-modal .pronto-radio-group {
		background: var(--neutral-0);
	}
}
</style>
